import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import Close from "../../assets/svg/CloseButton.svg";
import { usePostDetails } from "../../hooks/useBlog";
import AuthorBadge from "./AuthorBadge";
import { FiMessageCircle, FiHeart, FiShare2, FiMoreHorizontal, FiSend, FiEdit3, FiTrash2, FiLink } from "react-icons/fi";
import { hasRole, showToast } from "../../utils/utils";

const REACTION_TYPES = [
  { id: 0, emoji: "👍", label: "like", color: "bg-blue-50 text-blue-600" },
  { id: 1, emoji: "❤️", label: "love", color: "bg-red-50 text-red-600" },
  { id: 2, emoji: "😢", label: "sad", color: "bg-amber-50 text-amber-600" },
  { id: 3, emoji: "😠", label: "angry", color: "bg-orange-50 text-orange-600" },
];

const BlogPostModal = ({ postId, onClose, onEdit }) => {
  const user = useSelector(selectUserInformations);
  const isSuperAdmin = user?.roles && hasRole(user.roles, 'super-admin');

  const {
    post,
    comments,
    reactions,
    loading,
    totalComments,
    fetchPostData,
    addComment,
    deleteComment,
    toggleReaction,
    toggleCommentReaction,
    deletePost,
    loadMoreComments,
    commentsLoading,
    reactionLoading
  } = usePostDetails(postId);

  const [newComment, setNewComment] = useState("");
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [commentPage, setCommentPage] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [commentError, setCommentError] = useState("");

  useEffect(() => {
    if (postId) fetchPostData();
  }, [postId, fetchPostData]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    setCommentError("");

    if (!newComment.trim()) {
      setCommentError("Comment cannot be empty");
      return;
    }

    try {
      await addComment(newComment);
      setNewComment("");
      showToast("Success", "Comment posted!", "success");
    } catch (error) {
      console.error("Error adding comment:", error);
      showToast("Error", error?.response?.data?.message || "Could not post comment.", "error");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      showToast("Success", "Comment deleted.", "success");
    } catch (error) {
      showToast("Error", "Could not delete comment.", "error");
    }
  };

  const handleLoadMoreComments = () => {
    const nextPage = commentPage + 1;
    loadMoreComments(nextPage);
    setCommentPage(nextPage);
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/dashboard/blog?postId=${postId}`;
    navigator.clipboard.writeText(shareUrl);
    showToast("Link Copied!", "Share link has been copied to your clipboard.", "success");
    setShowOptionsMenu(false);
  };

  const handleDeletePost = async () => {
    if (window.confirm("Are you sure you want to delete this story? This action cannot be undone.")) {
      setIsDeleting(true);
      try {
        await deletePost();
        showToast("Deleted!", "Post deleted successfully.", "success");
        onClose();
        // Trigger a refresh of the posts list if needed
        window.location.reload(); // Quickest way to refresh all lists for now
      } catch (error) {
        showToast("Error", "Could not delete post.", "error");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const userReaction = reactions.find(r => r.user_uuid === user?.uuid);

  if (!postId) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-[8px] flex items-center justify-center z-[100] p-0 md:p-4 lg:p-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white w-full max-w-7xl h-full md:h-[95vh] md:rounded-[24px] shadow-2xl overflow-hidden relative flex flex-col md:flex-row"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button - Sticky/Fixed Mobile */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 transition-all group"
          >
            <img src={Close} alt="Close" className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:rotate-90 duration-300" />
          </button>

          {!loading && post ? (
            <>
              {/* Left Column: Image & Primary Content */}
              <div className="md:w-[70%] h-[40vh] md:h-full relative flex flex-col bg-gray-50 border-r border-gray-100">
                <div className="relative h-full w-full overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                  {/* Floating Content Over Image */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <span className="px-3 py-1 bg-blue-600/90 backdrop-blur-md text-white text-[10px] font-bold rounded-full tracking-wider uppercase">
                          {post.category?.name || 'Knowledge'}
                        </span>
                        <div className="flex items-center gap-1.5 text-white/70 text-xs font-medium bg-black/20 backdrop-blur-md px-3 py-1 rounded-full border border-white/10">
                          <FiMessageCircle size={14} />
                          {totalComments}
                        </div>
                      </div>
                      <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-4 drop-shadow-lg">
                        {post.title}
                      </h1>
                      <div className="flex items-center justify-between">
                        <AuthorBadge author={post.user} date={post.created_at} variant="light" />
                        <div className="flex items-center gap-3 relative">
                          <button
                            onClick={handleShare}
                            className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full text-white transition-all border border-white/10"
                            title="Share Link"
                          >
                            <FiShare2 size={18} />
                          </button>

                          {isSuperAdmin && (
                            <div className="relative">
                              <button
                                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                                className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full text-white transition-all border border-white/10"
                              >
                                <FiMoreHorizontal size={18} />
                              </button>

                              <AnimatePresence>
                                {showOptionsMenu && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                    className="absolute bottom-full right-0 mb-3 w-40 bg-white shadow-2xl rounded-2xl overflow-hidden z-50 p-2"
                                  >
                                    <button
                                      onClick={() => { onEdit(post); setShowOptionsMenu(false); }}
                                      className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-gray-700 hover:bg-blue-50 rounded-xl transition-all"
                                    >
                                      <FiEdit3 className="text-blue-500" /> Edit Post
                                    </button>
                                    <button
                                      onClick={handleDeletePost}
                                      disabled={isDeleting}
                                      className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50 rounded-xl transition-all disabled:opacity-50"
                                    >
                                      <FiTrash2 className="text-red-500" /> Delete Post
                                    </button>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Right Column: Reading & Interaction Section */}
              <div className="flex-1 flex flex-col h-full bg-white relative overflow-hidden">
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 custom-scrollbar relative">
                  {/* Article Body */}
                  <div className="relative mb-12">
                    <article
                      className={`prose prose-sm max-w-none text-gray-700 leading-relaxed font-normal antialiased overflow-hidden transition-all duration-700
                        ${!isContentExpanded && post.content?.length > 800 ? 'max-h-[400px]' : 'max-h-none'}`}
                    >
                      {post.content?.split('\n').map((para, i) => (
                        <p key={i} className="mb-4 text-[15px]">
                          {para}
                        </p>
                      ))}
                    </article>

                    {!isContentExpanded && post.content?.length > 800 && (
                      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/80 to-transparent flex items-end justify-center pb-2">
                        <button
                          onClick={() => setIsContentExpanded(true)}
                          className="px-6 py-2 bg-gray-900 text-white text-xs font-bold rounded-full shadow-xl hover:bg-blue-600 transition-all transform hover:scale-105"
                        >
                          Read Full Article
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Reaction Summary Bar */}
                  <div className="flex items-center justify-between py-4 border-y border-gray-50 mb-10">
                    <div className="flex -space-x-1.5 overflow-hidden">
                      {reactions.slice(0, 3).map((r, i) => (
                        <div key={i} className="w-6 h-6 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-xs shadow-sm">
                          {REACTION_TYPES[r.type]?.emoji || '🔥'}
                        </div>
                      ))}
                      {reactions.length > 3 && (
                        <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-50 flex items-center justify-center text-[10px] font-bold text-gray-400">
                          +{reactions.length - 3}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      <div className="relative">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          disabled={reactionLoading}
                          onClick={() => setShowReactionPicker(!showReactionPicker)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-bold
                            ${userReaction ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                            ${reactionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {userReaction ? (
                            <>
                              <span>{REACTION_TYPES[userReaction.type]?.emoji}</span>
                              Liked
                            </>
                          ) : (
                            <>
                              <FiHeart size={16} />
                              Like
                            </>
                          )}
                        </motion.button>

                        <AnimatePresence>
                          {showReactionPicker && (
                            <motion.div
                              initial={{ opacity: 0, y: 10, scale: 0.9 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: 10, scale: 0.9 }}
                              className="absolute bottom-full right-0 mb-3 p-1.5 bg-white shadow-2xl rounded-full border border-gray-100 flex gap-1 z-50 whitespace-nowrap"
                            >
                              {REACTION_TYPES.map((type) => (
                                <button
                                  key={type.id}
                                  disabled={reactionLoading}
                                  onClick={() => {
                                    toggleReaction(type.id);
                                    setShowReactionPicker(false);
                                  }}
                                  className={`p-2 text-xl hover:scale-125 transition-transform ${reactionLoading ? 'opacity-50' : ''} ${userReaction?.type === type.id ? 'bg-blue-50' : ''}`}
                                  title={type.label}
                                >
                                  {type.emoji}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>

                  {/* Conversations */}
                  <div className="space-y-8">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-bold text-gray-900">
                        Conversations <span className="text-gray-400 font-normal ml-1">({totalComments})</span>
                      </h3>
                    </div>

                    <div className="space-y-6">
                      {comments.map((comment) => {
                        const userCommentReaction = comment.reactions?.find(r => r.user_uuid === user?.uuid);
                        return (
                          <motion.div key={comment.id} layout className="group relative">
                            <div className="flex gap-3">
                              <div className="w-9 h-9 rounded-full bg-gray-100 flex-shrink-0 flex items-center justify-center font-bold text-xs text-gray-500 overflow-hidden border border-gray-200">
                                {comment.user?.avatar ? (
                                  <img src={comment.user.avatar} className="w-full h-full object-cover" alt="" />
                                ) : (
                                  comment.user?.name?.charAt(0) || 'U'
                                )}
                              </div>
                              <div className="flex-1 bg-gray-50/50 rounded-2xl px-4 py-3 border border-gray-100 transition-all hover:bg-gray-100/50">
                                <div className="flex items-center justify-between mb-1">
                                  <h4 className="text-sm font-bold text-gray-900">{comment.user?.name}</h4>
                                  <span className="text-[10px] font-medium text-gray-400">
                                    {comment.created_at}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                                  {comment.content}
                                </p>

                                <div className="flex items-center gap-3">
                                  <div className="flex items-center bg-white rounded-full p-0.5 border border-gray-100 shadow-sm">
                                    {REACTION_TYPES.map(type => (
                                      <button
                                        key={type.id}
                                        disabled={reactionLoading}
                                        onClick={() => toggleCommentReaction(comment.id, type.id)}
                                        className={`w-6 h-6 flex items-center justify-center text-xs rounded-full transition-all
                                          ${userCommentReaction?.type === type.id ? 'bg-blue-50 scale-110 shadow-sm' : 'hover:bg-gray-50'}
                                          ${reactionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      >
                                        {type.emoji}
                                      </button>
                                    ))}
                                  </div>
                                  {comment.reactions_count > 0 && (
                                    <span className="text-[10px] font-bold text-blue-600 px-2 py-0.5 rounded-full bg-blue-50">
                                      {comment.reactions_count}
                                    </span>
                                  )}

                                  {(isSuperAdmin || comment.user_uuid === user?.uuid) && (
                                    <button
                                      onClick={() => handleDeleteComment(comment.id)}
                                      className="text-[10px] font-bold text-red-400 hover:text-red-600 uppercase tracking-wider ml-auto transition-colors"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>

                    {/* Load More Comments Button */}
                    {totalComments > comments.length && (
                      <div className="flex justify-center pt-10">
                        <button
                          onClick={handleLoadMoreComments}
                          disabled={commentsLoading}
                          className="flex items-center gap-2 px-6 py-2 bg-gray-50 hover:bg-gray-100 text-gray-500 font-bold text-xs rounded-full border border-gray-100 transition-all active:scale-95"
                        >
                          {commentsLoading ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                          ) : (
                            "Show More Comments"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sticky Footer Comment Input */}
                <div className="p-4 md:p-6 bg-white border-t border-gray-100">
                  <form onSubmit={handleAddComment} className="flex items-start gap-3 relative">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex-shrink-0 flex items-center justify-center font-bold text-white text-[10px] overflow-hidden">
                      {user?.image_url ? (
                        <img src={user.image_url} className="w-full h-full object-cover" alt="" />
                      ) : (
                        user?.name?.charAt(0) || 'Me'
                      )}
                    </div>
                    <div className="flex-1 relative">
                      <textarea
                        value={newComment}
                        onChange={(e) => {
                          setNewComment(e.target.value);
                          if (commentError) setCommentError("");
                        }}
                        placeholder="Write a comment..."
                        className={`w-full bg-gray-50 border-none rounded-2xl py-3 px-4 pr-12 text-sm focus:ring-2 focus:ring-blue-600 transition-all font-medium min-h-[50px] max-h-[120px] resize-none ${commentError ? 'ring-2 ring-red-500' : ''}`}
                      />
                      {commentError && <p className="text-red-500 text-[10px] font-bold mt-1 px-1">{commentError}</p>}
                      <button
                        type="submit"
                        disabled={!newComment.trim()}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-blue-600 disabled:text-gray-300 transition-all hover:scale-110 disabled:scale-100"
                      >
                        <FiSend size={18} />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full w-full gap-5 bg-gray-50/50">
              <div className="relative">
                <div className="w-20 h-20 border-[3px] border-blue-100 rounded-full animate-pulse" />
                <div className="absolute inset-0 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="text-center">
                <p className="text-gray-900 font-bold text-lg">Curating Article</p>
                <p className="text-gray-400 text-sm">Gathering insights from the community...</p>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BlogPostModal;

